import { Tool } from "./tools";

export interface ToolDetail extends Tool {
  website: string;
  features: string[];
  logoUrl: string;
  affiliateUrl?: string;
  commission?: string;
}

export interface ToolReview {
  pros: string[];
  cons: string[];
  rating: number;
  review: string;
}

export interface SimilarTool {
  id: number;
  name: string;
  similarity: number;
}

export const toolDetails: Record<number, ToolDetail> = {
  1: {
    id: 1,
    name: "ChatGPT",
    description: "Advanced AI chatbot with natural language processing",
    price: "Freemium",
    rating: 4.8,
    categories: {
      function: ["Chatbot", "Text Generation"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Education", "Customer Service"]
    },
    createdAt: "2022-11-30",
    popularity: 95,
    website: "https://chat.openai.com",
    features: [
      "Natural language conversations",
      "Code generation and explanation",
      "Creative writing assistance",
      "Knowledge up to 2023"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=ChatGPT%20logo&sign=23c60cd7ededa85bf4c44efa9744cd2b"
  },
  2: {
    id: 2,
    name: "Midjourney",
    description: "AI-powered image generation from text prompts",
    price: "Paid",
    rating: 4.5,
    categories: {
      function: ["Image Generation", "Creative"],
      pricing: ["Paid"],
      industry: ["Design", "Marketing"]
    },
    createdAt: "2022-07-12",
    popularity: 88,
    website: "https://www.midjourney.com",
    features: [
      "High-quality image generation",
      "Diverse artistic styles",
      "Community features",
      "Continuous model improvements"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Midjourney%20logo&sign=98c39ab54e01504d0403f2141b9e77c9"
  },
  3: {
    id: 3,
    name: "Notion AI",
    description: "AI-powered workspace with smart organization",
    price: "Freemium",
    rating: 4.3,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2023-02-15",
    popularity: 82,
    website: "https://www.notion.so/product/ai",
    affiliateUrl: "https://notion.com/affiliates",
    commission: "$10-100 per signup",
    features: [
      "Smart document organization",
      "AI-powered writing assistance",
      "Task automation",
      "Knowledge management"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Notion%20AI%20logo&sign=0254539bb875b4c18e3530fd60fbc3d8"
  },
  4: {
    id: 4,
    name: "Claude",
    description: "AI assistant focused on helpful, harmless, and honest interactions",
    price: "Freemium",
    rating: 4.6,
    categories: {
      function: ["Chatbot", "Text Generation"],
      pricing: ["Freemium"],
      industry: ["Education", "Customer Service"]
    },
    createdAt: "2023-03-15",
    popularity: 85,
    website: "https://claude.ai",
    features: [
      "Helpful and honest responses",
      "Good at summarization",
      "Strong safety features",
      "Long context understanding"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Claude%20AI%20logo&sign=b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6"
  },
  5: {
    id: 5,
    name: "Gemini",
    description: "Google's advanced conversational AI service (formerly Bard)",
    price: "Free",
    rating: 4.4,
    categories: {
      function: ["Chatbot", "Text Generation"],
      pricing: ["Free"],
      industry: ["Education", "Search"]
    },
    createdAt: "2023-05-10",
    popularity: 80,
    website: "https://gemini.google.com/app",
    features: [
      "Web-connected responses",
      "Multilingual support",
      "Creative text generation",
      "Google integration"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Google%20Gemini%20logo&sign=7a1c23e58ebfd01e3c51d4c1999cad84"
  },
  6: {
    id: 6,
    name: "DALL-E",
    description: "AI system that can create realistic images from text descriptions",
    price: "Freemium",
    rating: 4.5,
    categories: {
      function: ["Image Generation", "Creative"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Design", "Marketing"]
    },
    createdAt: "2021-01-05",
    popularity: 87,
    website: "https://openai.com/dall-e",
    features: [
      "Photorealistic image generation",
      "Multiple image variations",
      "Editing existing images",
      "Commercial usage rights"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=DALL-E%20logo&sign=0b4ed3b5bdc2e2d9b8043cd91ff680a7"
  },
  7: {
    id: 7,
    name: "Stable Diffusion",
    description: "Open source text-to-image generation model",
    price: "Free",
    rating: 4.3,
    categories: {
      function: ["Image Generation", "Creative"],
      pricing: ["Free"],
      industry: ["Design", "Art"]
    },
    createdAt: "2022-08-22",
    popularity: 84,
    website: "https://stablediffusionweb.com",
    features: [
      "Open source model",
      "Local installation possible",
      "Highly customizable",
      "Active developer community"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Stable%20Diffusion%20logo&sign=331e9fccfe60255622c1dddb5c6816db"
  },
  8: {
    id: 8,
    name: "Grammarly",
    description: "AI-powered writing assistant for grammar and style",
    price: "Freemium",
    rating: 4.7,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2009-01-01",
    popularity: 90,
    website: "https://www.grammarly.com",
    affiliateUrl: "https://grammarly.com/affiliates",
    commission: "$20-200 per sale",
    features: [
      "Real-time grammar checking",
      "Style and tone suggestions",
      "Plagiarism detection",
      "Multi-platform support"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Grammarly%20logo&sign=79ec1421bfcd8a7a2a8642b450abad3c"
  },
  9: {
    id: 9,
    name: "Copy.ai",
    description: "AI-powered copywriting assistant for marketing content",
    price: "Freemium",
    rating: 4.2,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Marketing", "Business"]
    },
    createdAt: "2020-10-15",
    popularity: 78,
    website: "https://www.copy.ai",
    affiliateUrl: "https://copy.ai/partners",
    commission: "20-30% recurring",
    features: [
      "Marketing copy generation",
      "Multiple content templates",
      "Brand voice customization",
      "Team collaboration features"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Copy.ai%20logo&sign=f1b2f5bc6bdf9d1494b8ab7119dd08e0"
  },
  10: {
    id: 10,
    name: "Jasper",
    description: "AI content creator for businesses and marketers",
    price: "Subscription",
    rating: 4.4,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Subscription"],
      industry: ["Marketing", "Business"]
    },
    createdAt: "2021-02-01",
    popularity: 82,
    website: "https://www.jasper.ai",
    affiliateUrl: "https://jasper.ai/affiliate",
    commission: "30% first month + recurring",
    features: [
      "Long-form content creation",
      "SEO optimization",
      "Multi-language support",
      "Content templates"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Jasper%20AI%20logo&sign=98ddbac6e8e096b72c3dd8f640931bc3"
  },
  11: {
    id: 11,
    name: "Perplexity",
    description: "AI search engine with conversational interface",
    price: "Free",
    rating: 4.3,
    categories: {
      function: ["Chatbot", "Search"],
      pricing: ["Free"],
      industry: ["Education", "Research"]
    },
    createdAt: "2022-08-01",
    popularity: 79,
    website: "https://www.perplexity.ai",
    features: [
      "Conversational search",
      "Citation of sources",
      "Follow-up questions",
      "Multi-document analysis"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Perplexity%20AI%20logo&sign=c744d393fbf01e42d471ee11750a671f"
  },
  12: {
    id: 12,
    name: "Runway ML",
    description: "Creative toolkit powered by machine learning",
    price: "Freemium",
    rating: 4.1,
    categories: {
      function: ["Image Generation", "Video Editing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Design", "Art"]
    },
    createdAt: "2018-01-01",
    popularity: 75,
    website: "https://runwayml.com",
    features: [
      "Video editing with AI",
      "Image generation",
      "Style transfer",
      "Collaboration tools"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Runway%20ML%20logo&sign=b00d30231773c5d690727934fa188b2c"
  },
  13: {
    id: 13,
    name: "Fireflies",
    description: "AI notetaker for meetings and conversations",
    price: "Freemium",
    rating: 4.4,
    categories: {
      function: ["Productivity", "Transcription"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2019-05-15",
    popularity: 81,
    website: "https://fireflies.ai",
    features: [
      "Meeting transcription",
      "Action item detection",
      "Searchable archives",
      "Integration with calendar apps"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Fireflies%20AI%20logo&sign=a94cf2b656b9e328484fe720ec081726"
  },
  14: {
    id: 14,
    name: "Synthesia",
    description: "AI video generation platform with digital avatars",
    price: "Subscription",
    rating: 4.2,
    categories: {
      function: ["Video Generation", "Creative"],
      pricing: ["Subscription"],
      industry: ["Marketing", "Education"]
    },
    createdAt: "2017-01-01",
    popularity: 77,
    website: "https://www.synthesia.io",
    features: [
      "AI video creation",
      "Digital avatars",
      "Multi-language support",
      "Custom branding"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Synthesia%20logo&sign=48db4d755e3cd759788d798f1821e9ae"
  },
  15: {
    id: 15,
    name: "Otter.ai",
    description: "AI-powered voice meeting assistant",
    price: "Freemium",
    rating: 4.5,
    categories: {
      function: ["Productivity", "Transcription"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2016-01-01",
    popularity: 83,
    website: "https://otter.ai",
    features: [
      "Real-time transcription",
      "Speaker identification",
      "Meeting summaries",
      "Integration with Zoom"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Otter.ai%20logo&sign=3fc3de15b73bea9b1e62dbd2443bfd87"
  },
  16: {
    id: 16,
    name: "Descript",
    description: "AI-powered audio and video editing tool",
    price: "Freemium",
    rating: 4.3,
    categories: {
      function: ["Video Editing", "Audio Editing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Media", "Marketing"]
    },
    createdAt: "2017-01-01",
    popularity: 78,
    website: "https://www.descript.com",
    features: [
      "Text-based editing",
      "Screen recording",
      "AI voice cloning",
      "Collaboration features"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Descript%20logo&sign=5a0d3e4a91ddbee1d4c31fdedccbc32a"
  },
  17: {
    id: 17,
    name: "Murf",
    description: "AI voice generator for text-to-speech",
    price: "Freemium",
    rating: 4.2,
    categories: {
      function: ["Audio Generation", "Creative"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Media", "Education"]
    },
    createdAt: "2020-06-01",
    popularity: 76,
    website: "https://murf.ai",
    features: [
      "Natural sounding voices",
      "Multiple language options",
      "Voice customization",
      "Commercial usage rights"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Murf%20AI%20logo&sign=2dd1236aec6cffbeae7c03de15fa3734"
  },
  18: {
    id: 18,
    name: "Beautiful.ai",
    description: "AI-powered presentation design tool",
    price: "Subscription",
    rating: 4.1,
    categories: {
      function: ["Productivity", "Design"],
      pricing: ["Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2015-01-01",
    popularity: 74,
    website: "https://www.beautiful.ai",
    features: [
      "Smart slide templates",
      "Automatic design adjustments",
      "Team collaboration",
      "Brand consistency"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Beautiful.ai%20logo&sign=b35bb6c9d199ff049563f1fc9da22cf3"
  },
  19: {
    id: 19,
    name: "Tome",
    description: "AI-powered storytelling and presentation tool",
    price: "Freemium",
    rating: 4.0,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2020-01-01",
    popularity: 73,
    website: "https://tome.app",
    features: [
      "Narrative structure suggestions",
      "Automatic content generation",
      "Interactive elements",
      "3D presentation features"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Tome%20AI%20logo&sign=a792c8367ef521545ba000bab5de7d14"
  },
  20: {
    id: 20,
    name: "Quillbot",
    description: "AI-powered paraphrasing and writing tool",
    price: "Freemium",
    rating: 4.3,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Education", "Business"]
    },
    createdAt: "2017-01-01",
    popularity: 79,
    website: "https://quillbot.com",
    features: [
      "Advanced paraphrasing",
      "Grammar checking",
      "Summarization",
      "Citation generator"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Quillbot%20logo&sign=a8e4b51b67d89d3fd1a53f07bae37386"
  },
  21: {
    id: 21,
    name: "Krisp",
    description: "AI-powered noise cancellation for calls",
    price: "Freemium",
    rating: 4.6,
    categories: {
      function: ["Audio Processing", "Productivity"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2017-01-01",
    popularity: 85,
    website: "https://krisp.ai",
    features: [
      "Background noise removal",
      "Echo cancellation",
      "Works with any app",
      "CPU efficient"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Krisp%20AI%20logo&sign=1e30ac558567a31d12267c8fbdcb5c99"
  },
  22: {
    id: 22,
    name: "DeepL",
    description: "AI-powered translation tool with high accuracy",
    price: "Freemium",
    rating: 4.7,
    categories: {
      function: ["Translation", "Productivity"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2017-08-01",
    popularity: 86,
    website: "https://www.deepl.com",
    features: [
      "High-quality translations",
      "Multiple language support",
      "Document translation",
      "Context-aware"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=DeepL%20logo&sign=8945641c4a025e250b17c5d1f71a4b41"
  },
  23: {
    id: 23,
    name: "ElevenLabs",
    description: "AI voice generation and text-to-speech platform",
    price: "Freemium",
    rating: 4.4,
    categories: {
      function: ["Audio Generation", "Creative"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Media", "Education"]
    },
    createdAt: "2022-01-01",
    popularity: 80,
    website: "https://elevenlabs.io",
    features: [
      "Natural sounding voices",
      "Voice cloning",
      "Emotional speech synthesis",
      "API access"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=ElevenLabs%20logo&sign=62d8397449468a00104fec30980a03fe"
  },
  24: {
    id: 24,
    name: "Pictory",
    description: "AI video creation from text content",
    price: "Subscription",
    rating: 4.1,
    categories: {
      function: ["Video Generation", "Creative"],
      pricing: ["Subscription"],
      industry: ["Marketing", "Media"]
    },
    createdAt: "2019-01-01",
    popularity: 75,
    website: "https://pictory.ai",
    features: [
      "Text-to-video conversion",
      "Automatic captions",
      "Stock media library",
      "Brand customization"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Pictory%20AI%20logo&sign=9f562eb126901a47dc3396071480c34e"
  },
  25: {
    id: 25,
    name: "Chatsonic",
    description: "AI chatbot with web search capabilities",
    price: "Freemium",
    rating: 4.3,
    categories: {
      function: ["Chatbot", "Text Generation"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2022-12-01",
    popularity: 79,
    website: "https://writesonic.com/chat",
    features: [
      "Web-connected responses",
      "Image generation",
      "Multiple AI models",
      "API access"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Chatsonic%20logo&sign=fecd31421d3ff0b19519b338968d4855"
  },
  26: {
    id: 26,
    name: "Writesonic",
    description: "AI writing assistant for marketing content",
    price: "Freemium",
    rating: 4.2,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Marketing", "Business"]
    },
    createdAt: "2020-10-01",
    popularity: 77,
    website: "https://writesonic.com",
    features: [
      "Marketing copy generation",
      "SEO optimization",
      "Long-form content",
      "Multiple languages"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Writesonic%20logo&sign=cc66394aa3bc6b51c83722b066855ce6"
  },
  27: {
    id: 27,
    name: "Canva Magic Write",
    description: "AI-powered text generation for designs",
    price: "Freemium",
    rating: 4.1,
    categories: {
      function: ["Productivity", "Design"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Marketing", "Business"]
    },
    createdAt: "2022-10-01",
    popularity: 76,
    website: "https://www.canva.com/magic-write/",
    features: [
      "Design-focused text generation",
      "Tone adjustment",
      "Content suggestions",
      "Seamless Canva integration"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Canva%20Magic%20Write%20logo&sign=b0fe2d5ad60a05e19b046ee2fcf03d43"
  },
  28: {
    id: 28,
    name: "Adobe Firefly",
    description: "AI-powered creative tools for image generation",
    price: "Subscription",
    rating: 4.3,
    categories: {
      function: ["Image Generation", "Creative"],
      pricing: ["Subscription"],
      industry: ["Design", "Art"]
    },
    createdAt: "2023-03-01",
    popularity: 80,
    website: "https://www.adobe.com/sensei/generative-ai/firefly.html",
    features: [
      "Text-to-image generation",
      "Text effects",
      "Seamless Adobe integration",
      "Commercial-safe content"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Adobe%20Firefly%20logo&sign=459c86eabeee0ca5ab54e31b48634ae6"
  },
  29: {
    id: 29,
    name: "HeyGen",
    description: "AI video generation with digital avatars",
    price: "Subscription",
    rating: 4.0,
    categories: {
      function: ["Video Generation", "Creative"],
      pricing: ["Subscription"],
      industry: ["Marketing", "Education"]
    },
    createdAt: "2020-01-01",
    popularity: 74,
    website: "https://www.heygen.com",
    features: [
      "AI avatars",
      "Lip-sync technology",
      "Multi-language support",
      "Custom branding"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=HeyGen%20logo&sign=5c680af7ad04042133442b6fb5b7b104"
  },
  30: {
    id: 30,
    name: "Poe",
    description: "AI chatbot platform with multiple models",
    price: "Freemium",
    rating: 4.2,
    categories: {
      function: ["Chatbot", "Text Generation"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Education", "Business"]
    },
    createdAt: "2022-12-01",
    popularity: 78,
    website: "https://poe.com",
    features: [
      "Multiple AI models",
      "Web search capability",
      "Mobile app available",
      "Custom bot creation"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Poe%20AI%20logo&sign=4b2f2aabe111a22925bedf50bfe9d233"
  },
  31: {
    id: 31,
    name: "Gamma",
    description: "AI-powered presentation and document creation",
    price: "Freemium",
    rating: 4.1,
    categories: {
      function: ["Productivity", "Design"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2021-01-01",
    popularity: 75,
    website: "https://gamma.app",
    features: [
      "Smart document formatting",
      "Interactive elements",
      "Team collaboration",
      "Embeddable content"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Gamma%20AI%20logo&sign=b87a815d34811cf118b78ed5ae2dc6a2"
  },
  32: {
    id: 32,
    name: "Rewind",
    description: "AI-powered personal memory assistant",
    price: "Subscription",
    rating: 4.0,
    categories: {
      function: ["Productivity", "Search"],
      pricing: ["Subscription"],
      industry: ["Business", "Personal"]
    },
    createdAt: "2021-01-01",
    popularity: 73,
    website: "https://www.rewind.ai",
    features: [
      "Meeting recording",
      "Searchable history",
      "Privacy focused",
      "Cross-app integration"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Rewind%20AI%20logo&sign=e53f8a1776fa7567077fd7e1bcde55e4"
  },
  33: {
    id: 33,
    name: "Mem",
    description: "AI-powered note-taking and knowledge management",
    price: "Freemium",
    rating: 4.2,
    categories: {
      function: ["Productivity", "Writing"],
      pricing: ["Freemium", "Subscription"],
      industry: ["Business", "Education"]
    },
    createdAt: "2020-01-01",
    popularity: 77,
    website: "https://get.mem.ai",
    features: [
      "Smart note organization",
      "Automatic tagging",
      "Meeting notes generation",
      "Team collaboration"
    ],
    logoUrl: "https://space.coze.cn/api/coze_space/gen_image?image_size=square&prompt=Mem%20AI%20logo&sign=2ea5d0a40da58ed488a05aa8274601c2"
  }
};

export const toolReviews: Record<number, ToolReview> = {
  1: {
    pros: [
      "Highly conversational and responsive",
      "Wide range of knowledge",
      "Excellent for brainstorming"
    ],
    cons: [
      "Sometimes generates incorrect information",
      "Limited to knowledge up to 2023",
      "Can be verbose"
    ],
    rating: 4.8,
    review: "ChatGPT is one of the most advanced AI chatbots available today, offering impressive conversational abilities and a wide range of applications from creative writing to technical explanations."
  },
  2: {
    pros: [
      "Stunning image quality",
      "Diverse artistic styles",
      "Active community"
    ],
    cons: [
      "Steep learning curve",
      "Requires Discord",
      "Limited free tier"
    ],
    rating: 4.5,
    review: "Midjourney produces some of the most impressive AI-generated artwork available, with a wide range of styles and excellent detail, though the interface can be challenging for beginners."
  },
  3: {
    pros: [
      "Seamless Notion integration",
      "Useful writing assistance",
      "Good for knowledge management"
    ],
    cons: [
      "Limited standalone features",
      "Basic AI capabilities",
      "Not as powerful as specialized tools"
    ],
    rating: 4.3,
    review: "Notion AI adds useful AI features to an already excellent productivity platform, though it's not as powerful as standalone AI writing tools."
  },
  4: {
    pros: [
      "Helpful and honest responses",
      "Good at summarization",
      "Strong safety features"
    ],
    cons: [
      "Less creative than some alternatives",
      "Limited knowledge in some domains",
      "Can be overly cautious"
    ],
    rating: 4.6,
    review: "Claude provides reliable and safe AI interactions, with a focus on being helpful while avoiding harmful outputs. It excels at summarization and factual responses."
  },
  5: {
    pros: [
      "Web-connected knowledge",
      "Multilingual support",
      "Tight Google integration"
    ],
    cons: [
      "Sometimes lacks depth",
      "Limited customization",
      "Occasional factual errors"
    ],
    rating: 4.4,
    review: "Gemini (formerly Bard) offers convenient access to web-connected information with Google's backing, making it useful for up-to-date queries and multilingual conversations."
  },
  6: {
    pros: [
      "Photorealistic results",
      "Multiple image variations",
      "Commercial usage allowed"
    ],
    cons: [
      "Can be inconsistent",
      "Limited control over details",
      "Requires credits"
    ],
    rating: 4.5,
    review: "DALL-E produces high-quality, often photorealistic images from text prompts, with good commercial usage terms, though results can vary."
  },
  7: {
    pros: [
      "Open source",
      "Highly customizable",
      "Can run locally"
    ],
    cons: [
      "Requires technical knowledge",
      "Quality varies by model",
      "No official support"
    ],
    rating: 4.3,
    review: "Stable Diffusion is a powerful open-source alternative for those who want full control over their AI image generation, though it requires more technical expertise to use effectively."
  },
  8: {
    pros: [
      "Excellent grammar checking",
      "Real-time suggestions",
      "Wide platform support"
    ],
    cons: [
      "Expensive premium",
      "Can be overly aggressive",
      "Limited creative features"
    ],
    rating: 4.7,
    review: "Grammarly remains the gold standard for AI-powered writing assistance, particularly for grammar and style checking across multiple platforms."
  },
  9: {
    pros: [
      "Great for marketing copy",
      "Multiple templates",
      "Easy to use"
    ],
    cons: [
      "Generic sounding output",
      "Limited long-form ability",
      "Quality varies"
    ],
    rating: 4.2,
    review: "Copy.ai excels at generating short marketing copy with its template-based approach, though results can sometimes sound generic."
  },
  10: {
    pros: [
      "Good for long-form content",
      "SEO optimization",
      "Multiple languages"
    ],
    cons: [
      "Expensive",
      "Can be repetitive",
      "Requires careful editing"
    ],
    rating: 4.4,
    review: "Jasper is a capable AI writing tool for businesses needing long-form content, though it requires proper guidance to produce optimal results."
  },
  11: {
    pros: [
      "Cites sources",
      "Good for research",
      "Follow-up questions"
    ],
    cons: [
      "Limited knowledge",
      "Can be slow",
      "Basic interface"
    ],
    rating: 4.3,
    review: "Perplexity offers a more research-oriented alternative to traditional search engines with its citation features and conversational interface."
  },
  12: {
    pros: [
      "Creative video tools",
      "Style transfer",
      "Collaboration features"
    ],
    cons: [
      "Steep learning curve",
      "Expensive",
      "Requires good hardware"
    ],
    rating: 4.1,
    review: "Runway ML provides powerful creative tools for video editing and generation, though it requires some technical knowledge to use effectively."
  },
  13: {
    pros: [
      "Accurate transcriptions",
      "Action item detection",
      "Calendar integration"
    ],
    cons: [
      "Privacy concerns",
      "Expensive premium",
      "Occasional errors"
    ],
    rating: 4.4,
    review: "Fireflies is a capable meeting assistant that automatically transcribes and summarizes conversations, though some may have privacy concerns."
  },
  14: {
    pros: [
      "Realistic avatars",
      "Multi-language support",
      "Custom branding"
    ],
    cons: [
      "Limited customization",
      "Expensive",
      "Can look unnatural"
    ],
    rating: 4.2,
    review: "Synthesia provides an easy way to create professional-looking videos with AI avatars, though the results can sometimes appear unnatural."
  },
  15: {
    pros: [
      "Accurate transcriptions",
      "Speaker identification",
      "Zoom integration"
    ],
    cons: [
      "Limited free tier",
      "Privacy concerns",
      "Occasional errors"
    ],
    rating: 4.5,
    review: "Otter.ai remains one of the best meeting transcription tools available, with excellent accuracy and useful features like speaker identification."
  },
  16: {
    pros: [
      "Innovative text-based editing",
      "Screen recording",
      "Voice cloning"
    ],
    cons: [
      "Steep learning curve",
      "Expensive",
      "Resource intensive"
    ],
    rating: 4.3,
    review: "Descript offers innovative AI-powered editing features, particularly its text-based approach to audio and video editing."
  },
  17: {
    pros: [
      "Natural sounding voices",
      "Multiple languages",
      "Commercial usage"
    ],
    cons: [
      "Limited free tier",
      "Can sound robotic",
      "Expensive for high usage"
    ],
    rating: 4.2,
    review: "Murf provides high-quality text-to-speech voices suitable for commercial projects, though the free tier is quite limited."
  },
  18: {
    pros: [
      "Smart templates",
      "Automatic design",
      "Brand consistency"
    ],
    cons: [
      "Limited customization",
      "Expensive",
      "Basic AI features"
    ],
    rating: 4.1,
    review: "Beautiful.ai makes creating professional presentations quick and easy with its smart templates and automatic design adjustments."
  },
  19: {
    pros: [
      "Narrative structure",
      "Interactive elements",
      "3D features"
    ],
    cons: [
      "Limited templates",
      "Basic AI writing",
      "New platform"
    ],
    rating: 4.0,
    review: "Tome offers an innovative approach to presentations with its narrative-focused design and interactive elements."
  },
  20: {
    pros: [
      "Advanced paraphrasing",
      "Grammar checking",
      "Summarization"
    ],
    cons: [
      "Can change meaning",
      "Limited free tier",
      "Basic interface"
    ],
    rating: 4.3,
    review: "Quillbot is excellent for paraphrasing and summarizing text, though users should carefully review output to ensure meaning is preserved."
  },
  21: {
    pros: [
      "Excellent noise cancellation",
      "Works with any app",
      "CPU efficient"
    ],
    cons: [
      "Limited free tier",
      "Occasional artifacts",
      "Basic interface"
    ],
    rating: 4.6,
    review: "Krisp provides best-in-class noise cancellation for calls and recordings, working seamlessly with any application."
  },
  22: {
    pros: [
      "High-quality translations",
      "Document support",
      "Context-aware"
    ],
    cons: [
      "Limited free tier",
      "Fewer languages than competitors",
      "Expensive"
    ],
    rating: 4.7,
    review: "DeepL often produces more natural-sounding translations than competitors, particularly for European languages."
  },
  23: {
    pros: [
      "Natural voices",
      "Voice cloning",
      "Emotional speech"
    ],
    cons: [
      "Limited free tier",
      "Ethical concerns",
      "Expensive"
    ],
    rating: 4.4,
    review: "ElevenLabs offers some of the most natural-sounding AI voices available, with impressive emotional range and cloning capabilities."
  },
  24: {
    pros: [
      "Easy video creation",
      "Automatic captions",
      "Stock library"
    ],
    cons: [
      "Generic results",
      "Expensive",
      "Limited customization"
    ],
    rating: 4.1,
    review: "Pictory makes it easy to create simple videos from text content, though the results can look generic."
  },
  25: {
    pros: [
      "Web-connected",
      "Image generation",
      "Multiple models"
    ],
    cons: [
      "Generic responses",
      "Expensive",
      "Limited free tier"
    ],
    rating: 4.3,
    review: "Chatsonic combines web-connected responses with image generation in a single interface, though the quality varies."
  },
  26: {
    pros: [
      "Marketing focused",
      "SEO optimization",
      "Multiple languages"
    ],
    cons: [
      "Generic output",
      "Expensive",
      "Requires editing"
    ],
    rating: 4.2,
    review: "Writesonic is tailored for marketing content creation with useful SEO features, though output often requires refinement."
  },
  27: {
    pros: [
      "Design integration",
      "Tone adjustment",
      "Content suggestions"
    ],
    cons: [
      "Basic features",
      "Limited control",
      "Requires Canva"
    ],
    rating: 4.1,
    review: "Canva Magic Write adds useful AI text generation to the design process, though it's not as powerful as standalone writing tools."
  },
  28: {
    pros: [
      "Adobe integration",
      "Commercial-safe",
      "Professional results"
    ],
    cons: [
      "Requires subscription",
      "Limited styles",
      "New platform"
    ],
    rating: 4.3,
    review: "Adobe Firefly integrates seamlessly with Creative Cloud apps and produces professional-quality, commercially-safe AI images."
  },
  29: {
    pros: [
      "Realistic avatars",
      "Lip-sync",
      "Multi-language"
    ],
    cons: [
      "Limited expressions",
      "Expensive",
      "New platform"
    ],
    rating: 4.0,
    review: "HeyGen creates convincing AI avatar videos with good lip-sync technology, though the range of expressions is limited."
  },
  30: {
    pros: [
      "Multiple models",
      "Web search",
      "Mobile app"
    ],
    cons: [
      "Basic interface",
      "Limited free tier",
      "Quality varies"
    ],
    rating: 4.2,
    review: "Poe provides access to multiple AI models in one place, including web-connected options, though the interface is basic."
  },
  31: {
    pros: [
      "Smart formatting",
      "Interactive elements",
      "Team collaboration"
    ],
    cons: [
      "Limited templates",
      "Basic AI",
      "New platform"
    ],
    rating: 4.1,
    review: "Gamma offers an innovative approach to document creation with smart formatting and interactive elements."
  },
  32: {
    pros: [
      "Meeting recording",
      "Searchable history",
      "Privacy focused"
    ],
    cons: [
      "Expensive",
      "Mac only",
      "New platform"
    ],
    rating: 4.0,
    review: "Rewind provides a unique AI-powered memory assistant that records and makes your digital activity searchable."
  },
  33: {
    pros: [
      "Smart organization",
      "Automatic tagging",
      "Meeting notes"
    ],
    cons: [
      "Limited integrations",
      "Basic AI",
      "New platform"
    ],
    rating: 4.2,
    review: "Mem offers AI-powered note-taking with smart organization features, though it lacks some integrations."
  }
};

export const similarTools: Record<number, SimilarTool[]> = {
  1: [
    { id: 4, name: "Claude", similarity: 0.9 },
    { id: 5, name: "Gemini", similarity: 0.8 },
    { id: 11, name: "Perplexity", similarity: 0.7 }
  ],
  2: [
    { id: 6, name: "DALL-E", similarity: 0.9 },
    { id: 7, name: "Stable Diffusion", similarity: 0.8 },
    { id: 28, name: "Adobe Firefly", similarity: 0.7 }
  ],
  3: [
    { id: 8, name: "Grammarly", similarity: 0.8 },
    { id: 9, name: "Copy.ai", similarity: 0.7 },
    { id: 10, name: "Jasper", similarity: 0.7 }
  ],
  4: [
    { id: 6, name: "DALL-E", similarity: 0.9 },
    { id: 7, name: "Stable Diffusion", similarity: 0.8 },
    { id: 28, name: "Adobe Firefly", similarity: 0.7 }
  ],
  5: [
    { id: 8, name: "Grammarly", similarity: 0.8 },
    { id: 9, name: "Copy.ai", similarity: 0.7 },
    { id: 10, name: "Jasper", similarity: 0.7 }
  ],
  6: [
    { id: 4, name: "Midjourney", similarity: 0.9 },
    { id: 7, name: "Stable Diffusion", similarity: 0.8 },
    { id: 28, name: "Adobe Firefly", similarity: 0.7 }
  ],
  7: [
    { id: 4, name: "Midjourney", similarity: 0.8 },
    { id: 6, name: "DALL-E", similarity: 0.8 },
    { id: 28, name: "Adobe Firefly", similarity: 0.7 }
  ],
  8: [
    { id: 5, name: "Notion AI", similarity: 0.7 },
    { id: 20, name: "Quillbot", similarity: 0.7 },
    { id: 26, name: "Writesonic", similarity: 0.6 }
  ],
  9: [
    { id: 10, name: "Jasper", similarity: 0.8 },
    { id: 26, name: "Writesonic", similarity: 0.8 },
    { id: 27, name: "Canva Magic Write", similarity: 0.6 }
  ],
  10: [
    { id: 9, name: "Copy.ai", similarity: 0.8 },
    { id: 26, name: "Writesonic", similarity: 0.8 },
    { id: 5, name: "Notion AI", similarity: 0.6 }
  ],
  11: [
    { id: 1, name: "ChatGPT", similarity: 0.7 },
    { id: 4, name: "Claude", similarity: 0.7 },
    { id: 5, name: "Gemini", similarity: 0.7 }
  ],
  12: [
    { id: 14, name: "Synthesia", similarity: 0.7 },
    { id: 16, name: "Descript", similarity: 0.7 },
    { id: 24, name: "Pictory", similarity: 0.6 }
  ],
  13: [
    { id: 15, name: "Otter.ai", similarity: 0.9 },
    { id: 32, name: "Rewind", similarity: 0.7 },
    { id: 33, name: "Mem", similarity: 0.6 }
  ],
  14: [
    { id: 12, name: "Runway ML", similarity: 0.7 },
    { id: 16, name: "Descript", similarity: 0.6 },
    { id: 29, name: "HeyGen", similarity: 0.8 }
  ],
  15: [
    { id: 13, name: "Fireflies", similarity: 0.9 },
    { id: 32, name: "Rewind", similarity: 0.7 },
    { id: 33, name: "Mem", similarity: 0.6 }
  ],
  16: [
    { id: 12, name: "Runway ML", similarity: 0.7 },
    { id: 14, name: "Synthesia", similarity: 0.6 },
    { id: 17, name: "Murf", similarity: 0.7 }
  ],
  17: [
    { id: 16, name: "Descript", similarity: 0.7 },
    { id: 23, name: "ElevenLabs", similarity: 0.8 },
    { id: 21, name: "Krisp", similarity: 0.6 }
  ],
  18: [
    { id: 19, name: "Tome", similarity: 0.8 },
    { id: 31, name: "Gamma", similarity: 0.8 },
    { id: 27, name: "Canva Magic Write", similarity: 0.7 }
  ],
  19: [
    { id: 18, name: "Beautiful.ai", similarity: 0.8 },
    { id: 31, name: "Gamma", similarity: 0.8 },
    { id: 5, name: "Notion AI", similarity: 0.6 }
  ],
  20: [
    { id: 8, name: "Grammarly", similarity: 0.8 },
    { id: 5, name: "Notion AI", similarity: 0.7 },
    { id: 26, name: "Writesonic", similarity: 0.6 }
  ],
  21: [
    { id: 17, name: "Murf", similarity: 0.7 },
    { id: 15, name: "Otter.ai", similarity: 0.6 },
    { id: 13, name: "Fireflies", similarity: 0.6 }
  ],
  22: [
    { id: 23, name: "ElevenLabs", similarity: 0.6 },
    { id: 21, name: "Krisp", similarity: 0.5 },
    { id: 8, name: "Grammarly", similarity: 0.5 }
  ],
  23: [
    { id: 17, name: "Murf", similarity: 0.8 },
    { id: 21, name: "Krisp", similarity: 0.6 },
    { id: 14, name: "Synthesia", similarity: 0.5 }
  ],
  24: [
    { id: 14, name: "Synthesia", similarity: 0.7 },
    { id: 29, name: "HeyGen", similarity: 0.7 },
    { id: 12, name: "Runway ML", similarity: 0.6 }
  ],
  25: [
    { id: 1, name: "ChatGPT", similarity: 0.8 },
    { id: 30, name: "Poe", similarity: 0.8 },
    { id: 4, name: "Midjourney", similarity: 0.6 }
  ],
  26: [
    { id: 9, name: "Copy.ai", similarity: 0.9 },
    { id: 10, name: "Jasper", similarity: 0.8 },
    { id: 27, name: "Canva Magic Write", similarity: 0.7 }
  ],
  27: [
    { id: 26, name: "Writesonic", similarity: 0.7 },
    { id: 18, name: "Beautiful.ai", similarity: 0.7 },
    { id: 19, name: "Tome", similarity: 0.6 }
  ],
  28: [
    { id: 4, name: "Midjourney", similarity: 0.8 },
    { id: 6, name: "DALL-E", similarity: 0.8 },
    { id: 7, name: "Stable Diffusion", similarity: 0.7 }
  ],
  29: [
    { id: 14, name: "Synthesia", similarity: 0.8 },
    { id: 24, name: "Pictory", similarity: 0.7 },
    { id: 12, name: "Runway ML", similarity: 0.6 }
  ],
  30: [
    { id: 1, name: "ChatGPT", similarity: 0.8 },
    { id: 25, name: "Chatsonic", similarity: 0.8 },
    { id: 11, name: "Perplexity", similarity: 0.7 }
  ],
  31: [
    { id: 18, name: "Beautiful.ai", similarity: 0.8 },
    { id: 19, name: "Tome", similarity: 0.8 },
    { id: 5, name: "Notion AI", similarity: 0.6 }
  ],
  32: [
    { id: 13, name: "Fireflies", similarity: 0.7 },
    { id: 15, name: "Otter.ai", similarity: 0.7 },
    { id: 33, name: "Mem", similarity: 0.6 }
  ],
  33: [
    { id: 32, name: "Rewind", similarity: 0.7 },
    { id: 13, name: "Fireflies", similarity: 0.6 },
    { id: 15, name: "Otter.ai", similarity: 0.6 }
  ]
};
